import { useState } from 'react';

const usePagination = (props: {
  boundaryCount: number;
  currentPage: number;
  siblingCount: number;
  totalPages: number;
  onPageChange(pageIndex: number): void;
}) => {
  const { boundaryCount, onPageChange, currentPage, siblingCount, totalPages } = props;

  const [page, setPage] = useState(currentPage);

  const handlePageChange = (value: number) => {
    setPage(value);

    if (onPageChange) {
      onPageChange(value);
    }
  };

  // https://dev.to/namirsab/comment/2050
  const range = (start: number, end: number) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, Math.min(boundaryCount, totalPages));
  const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      totalPages - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : totalPages - 1
  );

  // Basic list of items to render
  // e.g. itemList = [1, 'ellipsis', 4, 5, 6, 'ellipsis', 10]
  const itemList = [
    ...startPages,

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2
      ? ['ellipsis']
      : boundaryCount + 1 < totalPages - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < totalPages - boundaryCount - 1
      ? ['ellipsis']
      : totalPages - boundaryCount > boundaryCount
      ? [totalPages - boundaryCount]
      : []),

    ...endPages
  ];

  // Convert the basic item list to button props
  const items = itemList.map((item: number | 'ellipsis') => {
    return typeof item === 'number'
      ? {
          onClick: () => {
            handlePageChange(item);
          },
          type: 'page',
          page: item,
          selected: item === page,
          disabled: false,
          'aria-current': item === page ? 'true' : undefined
        }
      : {
          onClick: () => {},
          type: item,
          page: 0,
          selected: false,
          disabled: true
        };
  });

  return {
    items,
    page,
    setPage: handlePageChange
  };
};

export { usePagination };
